import { useEffect, useState } from 'react';

/**
 * 17.2b — Cross-route window-scoped registry of dirty-draft entries.
 *
 * Document-editor + task-detail VMs register `{id, label}` whenever they
 * have a pending body/title past the last-saved snapshot, and unregister
 * on cleanup or when the draft reaches `saved`. `SignedInShell` reads the
 * registry before firing the org-switcher's `beginSwitch` action; if any
 * drafts are present, it opens a confirm modal listing each draft's label.
 *
 * Module-scoped state (deliberately not React state). Multiple VM
 * instances across mounted routes can register concurrently; the shell
 * snapshots `getUnsavedDrafts()` at click time. `subscribe(listener)` /
 * `useUnsavedDrafts()` are provided for surfaces that want a live view.
 */

export interface UnsavedDraft {
  id: string;
  label: string;
}

type Listener = (drafts: ReadonlyArray<UnsavedDraft>) => void;

const drafts = new Map<string, string>();
const listeners = new Set<Listener>();

function snapshot(): ReadonlyArray<UnsavedDraft> {
  return Array.from(drafts.entries()).map(([id, label]) => ({ id, label }));
}

function notify() {
  const next = snapshot();
  for (const listener of listeners) listener(next);
}

export function registerUnsavedDraft(draft: UnsavedDraft): () => void {
  drafts.set(draft.id, draft.label);
  notify();
  return () => {
    if (drafts.delete(draft.id)) notify();
  };
}

export function getUnsavedDrafts(): ReadonlyArray<UnsavedDraft> {
  return snapshot();
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only — drops every registered draft + clears subscribers. */
export function __resetUnsavedDraftsRegistryForTests() {
  drafts.clear();
  listeners.clear();
}

export function useUnsavedDrafts(): ReadonlyArray<UnsavedDraft> {
  const [state, setState] = useState<ReadonlyArray<UnsavedDraft>>(snapshot);
  useEffect(() => {
    setState(snapshot());
    return subscribe(setState);
  }, []);
  return state;
}

// Expose a small handle on `window` so Playwright specs can register drafts
// directly without having to drive a full document-editor / task-detail
// mount. The handle is read-only from the page's perspective; production
// code paths still go through `registerUnsavedDraft` directly.
if (typeof window !== 'undefined') {
  (window as unknown as {
    __penroseUnsavedDrafts?: {
      register: (draft: UnsavedDraft) => () => void;
      get: () => ReadonlyArray<UnsavedDraft>;
      reset: () => void;
    };
  }).__penroseUnsavedDrafts = {
    register: registerUnsavedDraft,
    get: getUnsavedDrafts,
    reset: __resetUnsavedDraftsRegistryForTests
  };
}
